import API from "../utilities/axiosConfig";
import { AlbumResponse, Photo } from "../types/photo";
import { PhotoLikeResponse, NearbyRequest } from "../types/photo";

const KoalaScoreThreshold = 0.7;

export const getPhoto = async (photoId: number): Promise<Photo> => {
	try {
		const response = await API.get(`/api/photo/${photoId}`)
		return response.data;
	} catch (error: any) {
		console.error("Fetch photo failed:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error("An unexpected error occurred. Please try again.");
		}
	}
};

export const getAllPhotos = async (): Promise<Photo[]> => {
	try {
		const response = await API.get(`/api/photo/all`)
		return response.data;
	} catch (error: any) {
		console.error("Fetch all photo failed:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error("An unexpected error occurred. Please try again.");
		}
	}
};

export const getPredictedPhotos = async (): Promise<Photo[]> => {
	try {
		const response = await API.get(`/api/photo/predicted/${KoalaScoreThreshold}`)
		return response.data;
	} catch (error: any) {
		console.error("Fetch predicted photo failed:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error("An unexpected error occurred. Please try again.");
		}
	}
};

export const getPhotoUrl = async (photoId: number): Promise<string> => {
	try {
		const response = await API.get(`/api/photo/${photoId}/url`);
		return response.data;
	} catch (error: any) {
		console.error("Fetch photo url failed:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error("An unexpected error occurred. Please try again.");
		}
	}
};

export const getPhotoUrlBatch = async (photoIds: number[]): Promise<string> => {
	try {
		const response = await API.post(`/api/photo/url/batch`, photoIds);
		return response.data;
	} catch (error: any) {
		console.error("Fetch photo url batch failed:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error("An unexpected error occurred. Please try again.");
		}
	}
};

export const getPhotoStream = async (photoId: number): Promise<Blob> => {
	try {
		const response = await API.get(`/api/photo/${photoId}/stream`, {
			responseType: "blob",
		});
		return response.data;
	} catch (error: any) {
		console.error("Fetch photo failed:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error("An unexpected error occurred. Please try again.");
		}
	}
};

export const getUserAlbum = async (page?: number, pageSize?: number): Promise<AlbumResponse> => {
	try {
		const response = await API.get(`/api/photo/user/album`, {
			params: page && pageSize ? { page, pageSize } : {},
		})
		return response.data as AlbumResponse;
	} catch (error: any) {
		console.error("Fetch album failed:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error("An unexpected error occurred. Please try again.");
		}
	}
};

export const uploadPhoto = async (formData: FormData): Promise<void> => {
	try {
		const response = await API.post("/api/photo", formData, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
	} catch (error: any) {
		console.error("Upload failed:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error("An unexpected error occurred. Please try again.");
		}
	}
};

export const uploadPhotoBatch = async (formData: FormData): Promise<any> => {
	try {
		const response = await API.post("/api/photo/batch", formData, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
		return response.data;
	} catch (error: any) {
		console.error("Upload failed:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error("An unexpected error occurred. Please try again.");
		}
	}
};

export const updatePhotoDetails = async (photoId: number, data: any): Promise<any> => {
	try {
		const response = await API.put(`/api/photo/${photoId}`, data);
		return response.data;
	} catch (error: any) {
		console.error("Update photo failed:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error("An unexpected error occurred. Please try again.");
		}
	}
}

export const deletePhoto = async (photoId: number): Promise<any> => {
	try {
		await API.delete(`/api/photo/${photoId}`);
	} catch (error: any) {
		console.error("Delete photo failed:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error("An unexpected error occurred. Please try again.");
		}
	}
}

export const photoLike = async (photoId: number): Promise<PhotoLikeResponse> => {
	try {
		const response = await API.post(`/api/photo/${photoId}/like`);
		return {
			photoId,
			liked: response.data.liked,
			likeCount: response.data.likeCount,
		};
	} catch (error: any) {
		console.error("Toggle like failed:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error("An unexpected error occurred. Please try again.");
		}
	}
};

export const getPhotoLikeStatus = async (photoId: number): Promise<PhotoLikeResponse> => {
	try {
		const response = await API.get(`/api/photo/${photoId}/like-status`);
		return {
			photoId,
			liked: response.data.liked,
			likeCount: response.data.likeCount,
		};
	} catch (error: any) {
		console.error("Get like status failed:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error("An unexpected error occurred. Please try again.");
		}
	}
};

export const getLatestPredictedPhotos = async (count = 15): Promise<Photo[]> => {
	try {
		const response = await API.get(`/api/photo/latest-predicted`, {
			params: { count },
		});
		return response.data;
	} catch (error: any) {
		console.error("Fetch latest predicted photos failed:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error("An unexpected error occurred. Please try again.");
		}
	}
};

export const getTopLikedPhotos = async (count = 8): Promise<Photo[]> => {
	try {
		const response = await API.get(`/api/photo/top-liked`, {
			params: { count },
		});
		return response.data;
	} catch (error: any) {
		console.error("Fetch top liked photos failed:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error("An unexpected error occurred. Please try again.");
		}
	}
};

export const getBatchPhotoLikeStatus = async (
	photoIds: number[]
): Promise<PhotoLikeResponse[]> => {
	try {
		const response = await API.post(`/api/photo/batch-like-status`, photoIds);
		return response.data;
	} catch (error: any) {
		console.error("Get batch like status failed:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error("An unexpected error occurred. Please try again.");
		}
	}
};

export const getNearbyKoalaPhotos = async (
	params: NearbyRequest
): Promise<Photo[]> => {
	try {
		const response = await API.post(`/api/nearby/photos`, params);
		return response.data;
	} catch (error: any) {
		console.error("Failed to fetch nearby koalas:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error("An unexpected error occurred. Please try again.");
		}
	}
};

export const getPhotoPredictionStatus = (photo: Photo) => {
	if (!photo.predictedDate) return 'processing';
	if (photo.predictions.some(p => p.score >= KoalaScoreThreshold)) return 'verified';
	if (photo.predictions.length === 0) return 'not-found';
	return 'uncertain'; // For predictions < 70
};